import { useState } from 'preact/hooks';
import { html } from 'htm/preact';
import useSWR from 'swr';
import fetch from 'unfetch';
import { parse } from 'preact-parser';
import produce from 'immer';

import usePages from './usePages';

const DISALLOWED_FILTERS = [];

const fetcher = (...args) => fetch(...args).then((res) => res.json());

export default function ProjectSearchWidget({
  postTypes = [], categories = [], translations = [], language = 'fi',
}) {
  // eslint-disable-next-line no-underscore-dangle
  const _t = (key) => translations[key] || key;
  const [selectedCategories, setSelectedCategories] = useState([{ id: '-1', name: 'all' }]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [sortOrder, setSortOrder] = useState('title_asc');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const selectedCategoryIds = selectedCategories.map((cat) => cat.id);

  // Get filters.
  const { data: filtersData } = useSWR(
    '/wp-json/odl/v1/project-filters/',
    fetcher,
  );

  // Get paginated projects.
  const {
    page: data,
    size,
    setSize,
    isValidating,
  } = usePages({
    postTypes, sortOrder, categories: selectedCategoryIds, filters: selectedFilters, language,
  });

  const pages = data ? [].concat(...data) : [];
  const itemCount = pages.reduce((prev, cur) => prev + cur.posts.length, 0);
  const totalPosts = data ? pages[0].total_posts : 0;

  const isLoadMoreDisabled = () => {
    if (totalPosts === 0) return true;
    if (data) return size === pages[0].pages;

    return true;
  };

  const onCategoryChange = ({ id = '-1', name = 'all' }) => {
    // Allow only one category.
    setSelectedCategories([{ id: id.toString(), name }]);
  };

  const onFilterChange = ({ filter = '', value = '' }) => {
    const nextState = produce(selectedFilters, (draft) => {
      if (!(filter in draft)) {
        // eslint-disable-next-line no-param-reassign
        draft[filter] = [];
      }

      if (draft[filter].includes(value)) {
        // eslint-disable-next-line no-param-reassign
        draft[filter] = draft[filter].filter((e) => e !== value);
      } else {
        draft[filter].push(value);
      }
    });

    setSelectedFilters(nextState);
  };

  const onSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const onClickOpenAdvancedSearch = () => {
    document.body.classList.add('advanced-search--open');
    setShowAdvancedSettings(true);
  };

  const onClickCloseAdvancedSearch = () => {
    document.body.classList.remove('advanced-search--open');
    setShowAdvancedSettings(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
  };

  return html`
    <div class="project-search__filter  search__filter  ${showAdvancedSettings ? 'is--shown' : ''}">
      <header class="search__header">${_t('search_filters')}</header>

      <form
        class="filter-group"
        onSubmit=${onSubmit}
      >
        ${categories && html`
          <div class="form__box">
            <header>${_t('categories')}</header>

            <label>
              <input
                type="radio"
                name="selected_categories[]"
                value="-1"
                onChange=${() => { onCategoryChange({ id: '-1', name: 'all' }); }}
                checked=${selectedCategoryIds.includes('-1')}
              />
              <span>${_t('all')}</span>
            </label>

            ${categories.map((cat) => html`
              <label key=${`category-filter-${cat.slug}`}>
                <input
                  type="radio"
                  name="selected_categories[]"
                  value=${cat.id}
                  data-name=${cat.slug}
                  onChange=${() => { onCategoryChange({ id: cat.term_id, name: cat.slug }); }}
                  checked=${selectedCategoryIds.includes(`${cat.term_id}`)}
                />
                <span>${cat.name}</span>
              </label>
            `)}
          </div>`}

        <div class="filter-group__expand-button">
          <button type="button" class="btn  btn--secondary" onClick=${onClickOpenAdvancedSearch}>
            <div class="button__contents">
              <span class="label">${_t('advanced_search_terms')}</span>
              <span class="icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.9999 8.18V3C19.9999 2.73478 19.8946 2.48043 19.707 2.29289C19.5195 2.10536 19.2651 2 18.9999 2C18.7347 2 18.4803 2.10536 18.2928 2.29289C18.1053 2.48043 17.9999 2.73478 17.9999 3V8.18C17.4208 8.3902 16.9204 8.77363 16.5668 9.27816C16.2131 9.7827 16.0234 10.3839 16.0234 11C16.0234 11.6161 16.2131 12.2173 16.5668 12.7218C16.9204 13.2264 17.4208 13.6098 17.9999 13.82V21C17.9999 21.2652 18.1053 21.5196 18.2928 21.7071C18.4803 21.8946 18.7347 22 18.9999 22C19.2651 22 19.5195 21.8946 19.707 21.7071C19.8946 21.5196 19.9999 21.2652 19.9999 21V13.82C20.5791 13.6098 21.0795 13.2264 21.4331 12.7218C21.7867 12.2173 21.9764 11.6161 21.9764 11C21.9764 10.3839 21.7867 9.7827 21.4331 9.27816C21.0795 8.77363 20.5791 8.3902 19.9999 8.18ZM18.9999 12C18.8021 12 18.6088 11.9414 18.4443 11.8315C18.2799 11.7216 18.1517 11.5654 18.076 11.3827C18.0003 11.2 17.9805 10.9989 18.0191 10.8049C18.0577 10.6109 18.153 10.4327 18.2928 10.2929C18.4327 10.153 18.6108 10.0578 18.8048 10.0192C18.9988 9.98063 19.1999 10.0004 19.3826 10.0761C19.5653 10.1518 19.7215 10.28 19.8314 10.4444C19.9413 10.6089 19.9999 10.8022 19.9999 11C19.9999 11.2652 19.8946 11.5196 19.707 11.7071C19.5195 11.8946 19.2651 12 18.9999 12ZM12.9999 14.18V3C12.9999 2.73478 12.8946 2.48043 12.707 2.29289C12.5195 2.10536 12.2651 2 11.9999 2C11.7347 2 11.4803 2.10536 11.2928 2.29289C11.1053 2.48043 10.9999 2.73478 10.9999 3V14.18C10.4208 14.3902 9.92037 14.7736 9.56676 15.2782C9.21314 15.7827 9.02344 16.3839 9.02344 17C9.02344 17.6161 9.21314 18.2173 9.56676 18.7218C9.92037 19.2264 10.4208 19.6098 10.9999 19.82V21C10.9999 21.2652 11.1053 21.5196 11.2928 21.7071C11.4803 21.8946 11.7347 22 11.9999 22C12.2651 22 12.5195 21.8946 12.707 21.7071C12.8946 21.5196 12.9999 21.2652 12.9999 21V19.82C13.5791 19.6098 14.0795 19.2264 14.4331 18.7218C14.7867 18.2173 14.9764 17.6161 14.9764 17C14.9764 16.3839 14.7867 15.7827 14.4331 15.2782C14.0795 14.7736 13.5791 14.3902 12.9999 14.18ZM11.9999 18C11.8021 18 11.6088 17.9414 11.4443 17.8315C11.2799 17.7216 11.1517 17.5654 11.076 17.3827C11.0003 17.2 10.9805 16.9989 11.0191 16.8049C11.0577 16.6109 11.153 16.4327 11.2928 16.2929C11.4327 16.153 11.6108 16.0578 11.8048 16.0192C11.9988 15.9806 12.1999 16.0004 12.3826 16.0761C12.5653 16.1518 12.7215 16.28 12.8314 16.4444C12.9413 16.6089 12.9999 16.8022 12.9999 17C12.9999 17.2652 12.8946 17.5196 12.707 17.7071C12.5195 17.8946 12.2651 18 11.9999 18ZM5.99991 6.18V3C5.99991 2.73478 5.89456 2.48043 5.70702 2.29289C5.51948 2.10536 5.26513 2 4.99991 2C4.7347 2 4.48034 2.10536 4.29281 2.29289C4.10527 2.48043 3.99991 2.73478 3.99991 3V6.18C3.42076 6.3902 2.92037 6.77363 2.56676 7.27817C2.21314 7.7827 2.02344 8.38388 2.02344 9C2.02344 9.61612 2.21314 10.2173 2.56676 10.7218C2.92037 11.2264 3.42076 11.6098 3.99991 11.82V21C3.99991 21.2652 4.10527 21.5196 4.29281 21.7071C4.48034 21.8946 4.7347 22 4.99991 22C5.26513 22 5.51948 21.8946 5.70702 21.7071C5.89456 21.5196 5.99991 21.2652 5.99991 21V11.82C6.57907 11.6098 7.07945 11.2264 7.43307 10.7218C7.78669 10.2173 7.97639 9.61612 7.97639 9C7.97639 8.38388 7.78669 7.7827 7.43307 7.27817C7.07945 6.77363 6.57907 6.3902 5.99991 6.18ZM4.99991 10C4.80213 10 4.60879 9.94135 4.44434 9.83147C4.27989 9.72159 4.15172 9.56541 4.07603 9.38268C4.00035 9.19996 3.98054 8.99889 4.01913 8.80491C4.05771 8.61093 4.15296 8.43275 4.29281 8.29289C4.43266 8.15304 4.61084 8.0578 4.80482 8.01921C4.9988 7.98063 5.19987 8.00043 5.3826 8.07612C5.56532 8.15181 5.7215 8.27998 5.83138 8.44443C5.94127 8.60888 5.99991 8.80222 5.99991 9C5.99991 9.26522 5.89456 9.51957 5.70702 9.70711C5.51948 9.89464 5.26513 10 4.99991 10Z" fill="currentColor"/>
                </svg>
              </span>
            </div>
          </button>
        </div>

        <div class="filter-group__advanced">
          ${filtersData && filtersData.filters.map((filter) => html`
            ${!(DISALLOWED_FILTERS.includes(filter.name)) && filter.choices && html`
              <div key=${`filter-${filter.key}`} class="form__box">
                <header>${filter.label}</header>

                ${filter.choices && Object.keys(filter.choices).map((key) => html`
                  <label key=${`filter-choice-${key}`}>
                    <input
                      type="checkbox"
                      name=${`${filter.name}[]`}
                      value=${key}
                      onChange=${() => { onFilterChange({ filter: filter.name, value: key }); }}
                      checked=${filter.name in selectedFilters && selectedFilters[filter.name].includes(key)}
                    />
                    <span>${filter.choices[key]}</span>
                  </label>
                `)}
              </div>
            `}
          `)}
        </div>

      </form>

      <div class="filter-group__close-button">
        <button type="button" class="btn  btn--primary" onClick=${onClickCloseAdvancedSearch}>
          <div class="button__contents">
            <span class="label">${_t('accept_search_terms')}</span>
            <span class="icon">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.2085 7.20986C19.1155 7.11613 19.0049 7.04174 18.8831 6.99097C18.7612 6.9402 18.6305 6.91406 18.4985 6.91406C18.3665 6.91406 18.2358 6.9402 18.1139 6.99097C17.992 7.04174 17.8814 7.11613 17.7885 7.20986L10.3385 14.6699L7.20849 11.5299C7.11196 11.4366 6.99802 11.3633 6.87317 11.3141C6.74831 11.2649 6.61499 11.2408 6.48081 11.2431C6.34663 11.2454 6.21422 11.2741 6.09114 11.3276C5.96806 11.3811 5.85672 11.4583 5.76348 11.5549C5.67025 11.6514 5.59693 11.7653 5.54773 11.8902C5.49852 12.015 5.47439 12.1484 5.47672 12.2825C5.47904 12.4167 5.50776 12.5491 5.56126 12.6722C5.61475 12.7953 5.69196 12.9066 5.78848 12.9999L9.62849 16.8399C9.72145 16.9336 9.83205 17.008 9.95391 17.0588C10.0758 17.1095 10.2065 17.1357 10.3385 17.1357C10.4705 17.1357 10.6012 17.1095 10.7231 17.0588C10.8449 17.008 10.9555 16.9336 11.0485 16.8399L19.2085 8.67986C19.31 8.58622 19.391 8.47257 19.4464 8.34607C19.5018 8.21957 19.5304 8.08296 19.5304 7.94486C19.5304 7.80676 19.5018 7.67015 19.4464 7.54365C19.391 7.41715 19.31 7.3035 19.2085 7.20986Z" fill="currentColor"/>
              </svg>
            </span>
          </div>
        </button>
      </div>
    </div>

    <div class="project-search__results  search__results" aria-live="polite">
      <div class="search__results__heading">
        <div class="left">
          ${pages.length > 0 && html`${_t('showing')} ${itemCount}/${totalPosts} ${_t('pcs')}`}
        </div>
        <div class="right">
          <label htmlFor="sort">${_t('order_by')}:</label>
          <select id="sort" name="sort" value=${sortOrder} onChange=${onSortChange}>
            <option value="title_asc">${_t('name')} (A-Z)</option>
            <option value="title_desc">${_t('name')} (Z-A)</option>
            <option value="date_desc">${_t('newest')}</option>
            <option value="date_asc">${_t('oldest')}</option>
          </select>
        </div>
      </div>

      ${pages.length > 0 && html`
        <div class="project-search__cards  search__cards  article-cards  is--layout-three-columns">
          ${pages.map((page) => html`
            ${parse(page.posts_rendered)}
          `)}
        </div>

        <div class="project-search__actions  search__actions">
          <button
            class="btn  btn--primary"
            disabled=${isValidating || isLoadMoreDisabled()}
            onClick=${() => setSize(size + 1)}
          >
            <div class="button__contents">
              <span class="title">${_t('load_more')}</span>
              <span class="icon">
                <svg
                  width="12"
                  height="14"
                  viewBox="0 0 12 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.92 6.62019C11.8724 6.49743 11.801 6.38529 11.71 6.29019L6.71 1.29019C6.61676 1.19695 6.50607 1.12299 6.38425 1.07253C6.26243 1.02207 6.13186 0.996094 6 0.996094C5.7337 0.996094 5.4783 1.10188 5.29 1.29019C5.19676 1.38342 5.1228 1.49411 5.07234 1.61594C5.02188 1.73776 4.99591 1.86833 4.99591 2.00019C4.99591 2.26649 5.1017 2.52188 5.29 2.71019L8.59 6.00019H1C0.734784 6.00019 0.48043 6.10554 0.292893 6.29308C0.105357 6.48062 0 6.73497 0 7.00019C0 7.2654 0.105357 7.51976 0.292893 7.70729C0.48043 7.89483 0.734784 8.00019 1 8.00019H8.59L5.29 11.2902C5.19627 11.3831 5.12188 11.4937 5.07111 11.6156C5.02034 11.7375 4.9942 11.8682 4.9942 12.0002C4.9942 12.1322 5.02034 12.2629 5.07111 12.3848C5.12188 12.5066 5.19627 12.6172 5.29 12.7102C5.38296 12.8039 5.49356 12.8783 5.61542 12.9291C5.73728 12.9798 5.86799 13.006 6 13.006C6.13201 13.006 6.26272 12.9798 6.38458 12.9291C6.50644 12.8783 6.61704 12.8039 6.71 12.7102L11.71 7.71019C11.801 7.61508 11.8724 7.50294 11.92 7.38019C12.02 7.13672 12.02 6.86365 11.92 6.62019Z"
                    fill="black"
                  />
                </svg>
              </span>
            </div>
          </button>
        </div>
      `}
    </div>
  `;
}
